import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["onSortedIndicatorChange", "column", "className", "children", "component", "defaultSortDirection", "direction", "buttonAriaDescribedById", "customStrings", "sortContainerStyles"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import clsx from "clsx";
import PropTypes from "prop-types";
import React from "react";
import { Button } from "../../";
import { KEYBOARD_KEYS } from "../../common/constants";
import STRINGS from "../strings";
import { getAriaHeaderProperties } from "./ariaUtils";
import { SORT_ASCENDING, SORT_DESCENDING, SORT_DIRECTIONS, SORT_UNSORTED } from "./constants";
import { toggleSortDirection } from "./utils";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";

function SortableHeaderCell(_ref) {
  let {
    onSortedIndicatorChange,
    // eslint-disable-line no-unused-vars
    column,
    // eslint-disable-line no-unused-vars
    className,
    children,
    component: HeaderElement,
    defaultSortDirection,
    // eslint-disable-line no-unused-vars
    direction,
    buttonAriaDescribedById,
    customStrings,
    // eslint-disable-line no-unused-vars, react/prop-types
    sortContainerStyles
  } = _ref,
      thProps = _objectWithoutProperties(_ref, _excluded);

  const handleSortToggle = e => {
    const key = e.key;

    if (key === KEYBOARD_KEYS.SPACE || key === KEYBOARD_KEYS.SPACE_IE11 || key === KEYBOARD_KEYS.ENTER || e.type === "click") {
      // prevent page scroll when hitting the space bar
      e.preventDefault();
      const newDirection = direction === SORT_UNSORTED ? defaultSortDirection : toggleSortDirection(direction);
      onSortedIndicatorChange({
        direction: newDirection,
        columnKey: column.columnKey
      });
    }
  };
  /*
   * Prevents the click event from firing on
   * `onKeyUp` event in order to prevent sorting
   * to happen again in Firefox.
   *
   * @param e {EventFacade} The event object
   */


  const handleKeyUp = e => {
    /**
     * Every browser fires a click event on key down of space
     * on a button. This can be avoided by preventing the default
     * action on `onKeyDown` event handler.
     * But, Firefox does not respect `e.preventDefault()` on the `onKeyDown`
     * event handler (still propagates to a click event).
     * so we need to prevent the default action on `onKeyUp`
     * event handler in order to not propagate a click event.
     * This prevents sorting to happen again in Firefox.
     * https://github.com/downshift-js/downshift/issues/471
     */
    e.preventDefault();
  };

  const {
    buttonStyles,
    sortIconStyles,
    sortContentStyles,
    sortedHeaderStyle
  } = sortContainerStyles;
  const isSorted = direction === SORT_ASCENDING || direction === SORT_DESCENDING;
  const sortDirectionStyles = isSorted && (direction === SORT_DESCENDING ? sortIconStyles === null || sortIconStyles === void 0 ? void 0 : sortIconStyles.sortedDescendingStyle : sortIconStyles === null || sortIconStyles === void 0 ? void 0 : sortIconStyles.sortedAscendingStyle);

  const sortStyles = _objectSpread(_objectSpread({}, sortIconStyles), sortDirectionStyles);

  const sortableClassName = isSorted ? [`pnr-table__col--sorted-${direction}`] : "pnr-table__sortable-unsorted-indicator";
  const thClassName = clsx(className, `pnr-table__col--sortable ${sortableClassName}`);
  const {
    ariaSortStatus
  } = getAriaHeaderProperties(isSorted, direction);
  return _jsx(HeaderElement, _objectSpread(_objectSpread({}, thProps), {}, {
    className: thClassName,
    "aria-sort": ariaSortStatus,
    onClick: handleSortToggle,
    onKeyDown: handleSortToggle,
    onKeyUp: handleKeyUp,
    css: isSorted ? sortedHeaderStyle : null,
    children: _jsxs(Button, {
      kind: "simple",
      className: "pnr-table__sortable-header-button",
      "aria-describedby": buttonAriaDescribedById,
      display: "block",
      buttonStyles: buttonStyles,
      children: [_jsx("span", {
        css: sortContentStyles,
        className: "pnr-table__sortable-indicator-header pnr-table__sortable-indicator",
        children: children
      }), _jsx("span", {
        className: "pnr-table__sortable-indicator-icon",
        css: sortStyles
      })]
    })
  }));
}

SortableHeaderCell.propTypes = process.env.NODE_ENV !== "production" ? {
  children: PropTypes.node.isRequired,
  column: PropTypes.shape({
    /** The key to locate the record */
    columnKey: PropTypes.string,

    /** The display label for the header or column information */
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
  }).isRequired,
  direction: PropTypes.oneOf(SORT_DIRECTIONS).isRequired,
  onSortedIndicatorChange: PropTypes.func.isRequired,

  /** aria-describedby for the sortable button */
  buttonAriaDescribedById: PropTypes.string,

  /** The CSS class name of the root element */
  className: PropTypes.string,

  /** node div or th or span to contain the header cell */
  component: PropTypes.node,
  defaultSortDirection: PropTypes.oneOf(SORT_DIRECTIONS),

  /**
   * Sorted header cell style (CSS-in-JS).
   */
  sortContainerStyles: PropTypes.shape()
} : {};
SortableHeaderCell.defaultProps = {
  className: "",
  component: "th",
  customStrings: STRINGS,
  buttonAriaDescribedById: null,
  defaultSortDirection: SORT_DESCENDING,
  sortContainerStyles: {}
};
export default SortableHeaderCell;